// frontend/src/components/ResultDisplay/ResultDisplay.jsx

import React, { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import "./ResultDisplay.css";
import { AuthContext } from "../../contexts/AuthContext";
import { AnalysisContext } from "../../contexts/AnalysisContext";
import { UIContext } from "../../contexts/UIContext";
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Tabs, Tab, IconButton, Button, CircularProgress, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import VerificationModal from '../VerificationModal/VerificationModal';
import StructuredExplanation from '../StructuredExplanation/StructuredExplanation';
import ScoreDonut from '../../pages/VerificationPage/ScoreDonut'; 

const getTaskDisplayName = (taskKey, t) => {
    return t(`resultDisplay.analysisTasks.${taskKey}`, { defaultValue: taskKey });
};

// --- Get the language name for the tooltip ---
const getLanguageName = (langCode) => {
    const lang = langCode.split('-')[0]; // Handle cases like 'en-US'
    const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
    return languageNames.of(lang);
};

// --- Clean Markdown (Same logic as ExplanationModal/VerificationPage) ---
const cleanMarkdown = (text) => {
    if (typeof text !== 'string') return text;

    let cleanText = text;
    // 1. Basic cleanup: Remove escaped asterisks
    cleanText = cleanText.replace(/\\\*/g, ''); 

    // 2. AGGRESSIVE HEADER NORMALIZATION
    // Pattern: Start -> Bullet -> Header Text (no colons) -> Colon -> Rest of Line
    cleanText = cleanText.replace(
        /(^|\n)([\*\-]\s+)([^:\n]+):\s*(.*)/g, // VETRA_AI_IGNORE_LINE
        (match, newline, bullet, headerContent, restOfLine) => {
            // 1. Clean the header: Remove existing asterisks, trim whitespace
            const cleanHeader = headerContent.replace(/\*/g, '').trim();
            
            // 2. Clean the body: Remove leading asterisks that might be left over (e.g. "** The body")
            let cleanBody = restOfLine.trim();
            // Remove up to 2 leading asterisks from body if they exist (fixing the "** Body" issue)
            cleanBody = cleanBody.replace(/^\*+\s*/, '');

            // 3. Reconstruct: Bullet + **Header:** + Space + Body
            return `${newline}${bullet}**${cleanHeader}:** ${cleanBody}`;
        }
    );
    return cleanText;
};



const ResultDisplay = ({ results, taskOrder }) => {
    const { t, i18n } = useTranslation();
    
    // FETCH masterTaskOrder so we can sort the tabs exactly like the Selector
    const { content, contentType, includeRegions, includeExplanation, contentPreviewUrl, masterTaskOrder } = useContext(AnalysisContext);
    
   const { user, fetchWithAuth } = useContext(AuthContext);
    const { setHeatmapModalData, setTextInspectionModalData } = useContext(UIContext);
    const isGuest = user?.email?.endsWith('@guest.local');
    const [activeTab, setActiveTab] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
    const [translatedExplanation, setTranslatedExplanation] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [isCheckingDescriptionCache, setIsCheckingDescriptionCache] = useState(false);
    const [translationError, setTranslationError] = useState(null);
    const [translatedDescription, setTranslatedDescription] = useState(null);
    const [isTranslatingDescription, setIsTranslatingDescription] = useState(false);
    const [isCheckingCache, setIsCheckingCache] = useState(false);

    // --- DATA NORMALIZATION AND TASK ORDERING ---
    const normalizedResults = { ...results };
    if (normalizedResults['vetra_score']) {
        // This part is correct: we create the consistent key.
        normalizedResults['digital_integrity_score'] = normalizedResults['vetra_score'];
        delete normalizedResults['vetra_score'];
    }

    // DYNAMIC SCORE MAPPING: Pass all available sub-task scores down to the Donut
    const subScores = {};
    Object.keys(normalizedResults).forEach(key => {
        if (typeof normalizedResults[key]?.explanation !== 'object') {
            subScores[key] = normalizedResults[key]?.score;
        }
    });

    // DYNAMIC TAB ORDERING & FILTERING
    const resultTasksInOrder = [];
    const metaTasks = [];
    const baseTasks = [];

    // 1. Categorize and filter results
    Object.keys(normalizedResults).forEach(key => {
        const res = normalizedResults[key];
        
        // --- ROBUST META-TASK CHECK ---
        const isMeta = res && (
            typeof res.explanation === 'object' || 
            typeof res.explanation_text === 'object' ||
            (res.model_id && String(res.model_id).includes('&')) ||
            (res.analysis_id && String(res.analysis_id).startsWith('Derived'))
        );
        
        if (isMeta) {
            // Apply user preference filter
            const enabledMetaTasks = user?.analysis_preferences?.enabled_meta_tasks;
            
            // If the user has preferences saved, and this task IS NOT in the enabled list, skip it!
            if (Array.isArray(enabledMetaTasks) && !enabledMetaTasks.includes(key)) {
                return; 
            }
            metaTasks.push(key);
        } else {
            baseTasks.push(key);
        }
    });

    // 2. Sort tasks to match the Selector/YAML order exactly
    const sortTasksByMasterOrder = (a, b) => {
        // Fallback to taskOrder (user's selection array) if master is somehow missing
        const orderList = (masterTaskOrder && masterTaskOrder.length > 0) ? masterTaskOrder : taskOrder;
        
        // Final fallback: standard alphabetical sorting
        if (!orderList || orderList.length === 0) return a.localeCompare(b);
        
        const indexA = orderList.indexOf(a);
        const indexB = orderList.indexOf(b);
        
        // If neither are in the master list, alphabetize them
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        // If one is missing from the master list, put it at the end
       if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
    };

    metaTasks.sort(sortTasksByMasterOrder);
    baseTasks.sort(sortTasksByMasterOrder);
    // 3. Combine them: Meta tasks first, then base tasks
    resultTasksInOrder.push(...metaTasks);
    resultTasksInOrder.push(...baseTasks);

    const currentActiveTab = Math.max(0, Math.min(activeTab, resultTasksInOrder.length - 1));
    const activeTaskKey = resultTasksInOrder[currentActiveTab];
    const activeResult = normalizedResults[activeTaskKey];

    // --- DYNAMIC METADATA LOOKUP ---
    const getMetadataSafe = () => {
        // Try to find ANY task result that contains content_metadata
        const foundTaskWithMetadata = Object.values(normalizedResults).find(res => res?.content_metadata);
        return foundTaskWithMetadata?.content_metadata || activeResult?.content_metadata || null;
    };

    const activeMetadata = getMetadataSafe();
    const rawExplanation = activeResult ? (activeResult.explanation || activeResult.explanation_text || "") : "";
    
    // --- ROBUST META-TASK CHECK ---
    const isMetaTask = activeResult && (
        typeof rawExplanation === 'object' ||
        (activeResult.model_id && String(activeResult.model_id).includes('&')) ||
        (activeResult.analysis_id && String(activeResult.analysis_id).startsWith('Derived'))
    ); 

    // This runs whenever language or metadata changes.
    const checkAndApplyCachedDescription = useCallback(async () => {
        // Skip if English, no summary, or we already have it
        if (i18n.language.startsWith('en') || !activeMetadata?.summary) {
            setTranslatedDescription(null); // Reset to English
            return;
        }

        setIsCheckingDescriptionCache(true);
        try {
            // We reuse the existing /api/translate endpoint with cache_only=true
            // This is safer than using the public GET endpoint inside the vetra app flow
            const response = await fetchWithAuth('/api/translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: activeMetadata.summary,
                    target_language: i18n.language,
                    cache_only: true // <--- Key parameter
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.translated_text) {
                    // Cache hit! Apply immediately.
                    setTranslatedDescription(data.translated_text);
                } else {
                    // Cache miss. Reset so English shows + Button appears.
                    setTranslatedDescription(null);
                }
            }
        } catch (err) {
            console.error("Description cache check failed:", err);
        } finally {
            setIsCheckingDescriptionCache(false);
        }
    }, [activeMetadata, i18n.language, fetchWithAuth]);

    // Trigger the check
    useEffect(() => {
        setTranslatedDescription(null);
        checkAndApplyCachedDescription();
    }, [checkAndApplyCachedDescription]); 

    // --- Handle Description Translation ---
    const handleTranslateDescription = async () => {
        if (!activeMetadata?.summary) return;
        setIsTranslatingDescription(true);
        try {
            const response = await fetchWithAuth('/api/translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: activeMetadata.summary,
                    target_language: i18n.language
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Translation failed');
            setTranslatedDescription(data.translated_text);
        } catch (err) {
            console.error("Description translation error:", err);
        } finally {
            setIsTranslatingDescription(false);
        }
    };

    // --- AUTOMATIC CACHE CHECK ---
    const checkAndApplyCachedTranslation = useCallback(async () => {
        // Don't run this for English, for the structured explanation, or if there's no text
        if (i18n.language.startsWith('en') || isMetaTask || !rawExplanation) {
            return;
        }
        setIsCheckingCache(true);
        try {
            const response = await fetchWithAuth('/api/translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: rawExplanation,
                    target_language: i18n.language,
                    cache_only: true
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.translated_text) {
                    // Cache hit! Apply the translation automatically.
                    setTranslatedExplanation(data.translated_text);
                }
                // If there's no translated_text, it was a cache miss. Do nothing, let the user click the button.
            }
        } catch (err) {
            console.error("Cache check failed:", err);
            // Don't show an error, just fall back to manual translation.
        } finally {
            setIsCheckingCache(false);
        }
    }, [rawExplanation, i18n.language, isMetaTask, fetchWithAuth]);

    // We add `i18n.language` to the dependency array.
    useEffect(() => {
        setShowExplanation(false);
        setTranslatedExplanation(null);
        setIsTranslating(false);
        setTranslationError(null);
        setIsCheckingCache(false);
        checkAndApplyCachedTranslation();
    }, [activeResult, i18n.language, checkAndApplyCachedTranslation]); // Now it resets on result change OR language change.
    if (resultTasksInOrder.length === 0 || !activeResult) return null;
    const handleTabChange = (event, newValue) => { setActiveTab(newValue); };

    const handleTranslate = async () => {
        if (!rawExplanation) return;
        setIsTranslating(true);
        setTranslationError(null);
        try {
            const response = await fetchWithAuth('/api/translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: rawExplanation,
                    target_language: i18n.language
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Translation request failed');
            setTranslatedExplanation(data.translated_text);
        } catch (err) {
            console.error("Translation error:", err);
            setTranslationError(err.message);
        } finally {
            setIsTranslating(false);
        }
    };
    const showInspectSection = includeRegions && !isMetaTask && (contentType === 'image' || contentType === 'text' || contentType === 'video');
    const manipulationRegions = activeResult?.manipulation_regions;

    // Dynamic determination of regions availability for the CURRENT tab
    const hasRegionsToShow = (contentType === 'image' && activeResult.manipulation_regions?.length > 0) || 
                             (contentType === 'text' && activeResult.manipulation_regions?.length > 0) || 
                             (contentType === 'video' && activeResult.manipulation_regions?.boxes?.length > 0);
    
    // 1. Base Params
    const contextHash = activeResult?.context_hash;
    const tasksQueryString = taskOrder.map(task => `tasks=${encodeURIComponent(task)}`).join('&');
    const tasksParamForDirectLink = taskOrder.map(task => `context=${encodeURIComponent(task)}`).join('&');

    // 2. New Params
    const optionsQueryString = `regions=${includeRegions}&explanation=${includeExplanation}`;

    // --- NEW: Attribution Param ---
    // Extract public_id from the vetra user object
    const attributionParam = user?.public_id ? `&ref=${user.public_id}` : '';

    // 3. Direct Verification Link (What user clicks)
    let verificationUrl = `/verify?id=${activeResult.exact_hash}`;
    
    // Append ref FIRST (good practice, though order doesn't strictly matter)
    verificationUrl += attributionParam; 

    if (contextHash) verificationUrl += `&ctx=${contextHash}`;
    if (tasksParamForDirectLink) verificationUrl += `&${tasksParamForDirectLink}`;
    verificationUrl += `&${optionsQueryString}`; // Add to direct link

    // 4. QR Code Image URL (API Call)
    // IMPORTANT: The backend 'get_qr_code_image' endpoint uses the logged-in user's ID
    let qrCodeImageUrl = `/api/qr/${activeResult.exact_hash}`;
    const queryParts = [];
    if (user?.public_id) {
        queryParts.push(`ref=${user.public_id}`);
    }
    if (contextHash) queryParts.push(`ctx=${contextHash}`);
    if (tasksQueryString) queryParts.push(tasksQueryString);
    queryParts.push(optionsQueryString); // Add to QR generator request

    if (queryParts.length > 0) {
        qrCodeImageUrl += `?${queryParts.join('&')}`;
    }


    // 1. Determine which text is currently active (translated or original).
    const activeText = translatedExplanation || rawExplanation;
    // 2. Base the visibility of the "Show More" button on the *active* text's length.
    //    We also ensure it only applies to plain string explanations, not the structured one.
    const isLongText = typeof activeText === 'string' && activeText.length > 200;
    const showMoreButtonVisible = isLongText && !isMetaTask;

    const handleInspectClick = () => {
        const taskName = getTaskDisplayName(activeTaskKey, t);
        if (contentType === 'image') {
            setHeatmapModalData({ imageSrc: contentPreviewUrl, regions: manipulationRegions, taskName: taskName, score: activeResult.score, mode: 'full' });
        }
        else if (contentType === 'video') {
            const videoNote = t('heatmap.footerNoteVideo', { time: manipulationRegions.key_frame_time.toFixed(2) });
            setHeatmapModalData({ imageSrc: manipulationRegions.key_frame_thumbnail || contentPreviewUrl, regions: manipulationRegions.boxes, taskName: taskName, score: activeResult.score, mode: 'full', footerNote: videoNote, contentType: contentType });
        }
        else if (contentType === 'text') {
            setTextInspectionModalData({ text: content, regions: manipulationRegions, taskName: taskName, score: activeResult.score });
        }
    };

    return (
        <>
            <div className="results-container">
                {!isGuest && (
                    <Tooltip
                        title={t('resultDisplay.shieldTooltip')}
                        arrow
                        // Add PopperProps to control where the tooltip is rendered in the DOM.
                        PopperProps={{
                            // This tells the Tooltip to attach itself near the button
                            // instead of at the end of the document body.
                            disablePortal: true,
                        }}
                    >
                        <IconButton className="verification-shield-button" onClick={() => setIsVerificationModalOpen(true)}>
                            <img
                                src="/static/vetraai_logo_grey.png"
                                alt="Verification Shield"
                                className="shield-icon-image"
                            />
                        </IconButton>
                    </Tooltip>
                )}
                <Box className="results-header-container">
                    <Box
                        sx={{ borderBottom: 1, borderColor: "divider" }}
                        className="results-tabs-container"
                    >
                        <Tabs
                            value={currentActiveTab}
                            onChange={handleTabChange}
                            aria-label="analysis results tabs"
                            // Dynamically switch variants: scrollable for many, standard for 1
                            variant={resultTasksInOrder.length > 1 ? "scrollable" : "standard"}
                            // Center it ONLY if there is exactly 1 task
                            centered={resultTasksInOrder.length === 1}
                            // Only show scroll buttons if there are multiple tabs
                            scrollButtons={resultTasksInOrder.length > 1 ? "auto" : false}
                            allowScrollButtonsMobile={resultTasksInOrder.length > 1}
                        >
                            {resultTasksInOrder.map((taskKey) => {
                                const isMetaTaskTab = typeof normalizedResults[taskKey]?.explanation === 'object';
                                // We don't have tasksConfig in ResultDisplay, so we just use the translation file
                                const tooltipText = t(`verificationPage.scoreDefinitions.${taskKey}`);

                                return (
                                    <Tab
                                        key={taskKey}
                                        icon={isMetaTaskTab ? <img src="/static/vetraai_logo_grey.png" alt="Integrity Score" className="tab-logo-icon" /> : undefined}
                                        iconPosition="start"
                                        label={
                                            <Tooltip title={tooltipText} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <span>{getTaskDisplayName(taskKey, t)}</span>
                                                </Box>
                                            </Tooltip>
                                        }
                                        className={`results-tab ${isMetaTaskTab ? 'meta-task-tab' : ''}`}
                                    />
                                );
                            })}
                        </Tabs>
                    </Box>
                </Box>
                <div className="analysis-record">
                    {/* --- Likelihood & Score Items with Donut --- */}
                    <div className="score-donut-center-wrapper">
                        <ScoreDonut 
                            riskScore={activeResult.score} 
                            scoreType={activeTaskKey} 
                            subScores={subScores}
                            t={t} 
                            size={140} 
                        />

                        {activeMetadata && (
                            <div className="content-metadata-container" style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '1rem', width: '100%' }}>
                                
                                {/* 1. Description */}
                                {activeMetadata.summary && (
                                    <div className="metadata-description-block">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <span className="metadata-label" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>
                                                {t('contentMetadata.descriptionLabel')}
                                            </span>
                                            {/* --- TRANSLATE BUTTON FOR DESCRIPTION --- */}
                                            {/* Only show if:
                                                1. Not English
                                                2. Not actively checking cache (prevents flickering)
                                                3. No translation currently applied (either from cache or manual)
                                            */}
                                            {!i18n.language.startsWith('en') && !isCheckingDescriptionCache && !translatedDescription && (
                                                <Tooltip title={t('resultDisplay.translateTooltip', { language: getLanguageName(i18n.language) })}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            className="translate-explanation-button"
                                                            onClick={handleTranslateDescription}
                                                            disabled={isTranslatingDescription} // Just disable it, no spinner inside
                                                            sx={{ 
                                                                padding: '2px',
                                                                color: 'inherit'
                                                            }}
                                                        >
                                                            <GTranslateIcon sx={{ fontSize: '1rem' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            
                                            {/* Optional: Show a tiny spinner while checking cache silently? 
                                                Usually not needed for cache checks as they are fast, but good for debugging. 
                                            */}
                                        </div>

                                        <p className="metadata-summary-text" style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)', fontStyle: 'italic', margin: 0, lineHeight: 1.5 }}>
                                            {/* Show spinner if checking cache, otherwise show text */}
                                            {isCheckingDescriptionCache ? (
                                                <span style={{ opacity: 0.5 }}>{t('resultDisplay.checkingTranslation')}</span>
                                            ) : isTranslatingDescription ? (
                                                /* --- LOADING STATE IN TEXT AREA --- */
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', opacity: 0.8, fontStyle: 'normal' }}>
                                                    <CircularProgress size={12} thickness={6} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                                    <span>{t('resultDisplay.translatingMessage')}</span>
                                                </span>
                                            ) : (
                                                `"${translatedDescription || activeMetadata.summary}"`
                                            )}
                                            
                                            {/* Category Tag - Hide while translating to avoid visual clutter */}
                                            {!isTranslatingDescription && activeMetadata.category && (
                                                <span className="metadata-category-tag" style={{ color: 'var(--secondary-color)', fontWeight: 700, fontStyle: 'normal' }}>
                                                    &nbsp;&nbsp;({t(`contentMetadata.inclusion.${activeMetadata.category}`, { 
                                                        defaultValue: activeMetadata.category 
                                                    })})
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                {/* 2. Safety Flags */}
                                {activeMetadata.safety_flags && 
                                activeMetadata.safety_flags.length > 0 && (
                                    <div className="metadata-safety-alert" style={{ marginTop: '0.2rem' }}>
                                        <ReportProblemOutlinedIcon fontSize="small" />
                                        <div className="safety-flag-list">
                                            {/* Use localized prefix */}
                                            <strong>{t('metaScores.safetyAlertPrefix')}</strong>&nbsp;
                                            {activeMetadata.safety_flags.map((flagKey, index) => (
                                                <span key={index}>
                                                    {t(`contentMetadata.exclusion.${flagKey}`, { defaultValue: flagKey })}
                                                    {index < activeMetadata.safety_flags.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {rawExplanation && (
                        <div className="result-item explanation-item">
                            <div className="result-label-wrapper explanation-label-interactive">
                                <h3 className="result-label">{t('resultDisplay.explanation')}:</h3>
                                {typeof rawExplanation === 'string' && !i18n.language.startsWith('en') && !translatedExplanation && !isCheckingCache && (
                                    <Tooltip title={t('resultDisplay.translateTooltip', { language: getLanguageName(i18n.language) })}>
                                        <IconButton
                                            size="small"
                                            className="translate-explanation-button"
                                            onClick={handleTranslate}
                                            disabled={isTranslating}
                                        >
                                            {/* The disabled prop handles isTranslating, so we don't need it in the outer condition */}
                                            <GTranslateIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                            <div className="result-value-wrapper">
                                {isCheckingCache ? (
                                    <div className="translation-loader minimal">
                                        <CircularProgress size={24} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                    </div>
                                ) : isTranslating ? (
                                    <div className="translation-loader">
                                        <CircularProgress size={24} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                        <p className="translation-loader-message">
                                            {t('resultDisplay.translatingMessage')}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {/* 
                                           ADD THE DYNAMIC CLASS 'collapsed' here.
                                           If showExplanation is FALSE and it is long text, we add 'collapsed'.
                                        */}
                                        <div className={`explanation-content ${!showExplanation && showMoreButtonVisible ? 'collapsed' : ''}`}>
                                            {isMetaTask ? (
                                                <StructuredExplanation
                                                    data={activeResult.explanation}
                                                    score={activeResult.score}
                                                    contentMetadata={activeMetadata}
                                                />
                                            ) : (
                                                /* Always render full activeText */
                                                <ReactMarkdown 
                                                    remarkPlugins={[remarkGfm]}
                                                    components={{
                                                        // Consistent styling components
                                                        strong: ({node, ...props}) => <span style={{ fontWeight: '700'}} {...props} />,
                                                        em: ({node, ...props}) => <span style={{ fontWeight: '700', fontStyle: 'normal'}} {...props} />
                                                    }}
                                                >
                                                    {DOMPurify.sanitize(cleanMarkdown(activeText))}
                                                </ReactMarkdown>
                                            )}
                                        </div>

                                        <div className="explanation-actions">
                                            {translationError && <p className="translation-error">Could not translate: {translationError}</p>}
                                            {showMoreButtonVisible && !isMetaTask &&
                                                <button className="toggle-explanation-button" onClick={() => setShowExplanation(!showExplanation)}>
                                                    {showExplanation ? t('resultDisplay.showLess') : t('resultDisplay.showMore')}
                                                </button>
                                            }
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {showInspectSection && (
                        <div className="result-item details-button-item"> 
                            <div className="result-label-wrapper"> 
                                <h3 className="result-label">{t('resultDisplay.inspectDetails')}:</h3> 
                            </div> 
                            <div className="result-value-wrapper"> 
                                {hasRegionsToShow ? (
                                    <Button 
                                        variant="outlined" 
                                        startIcon={<SearchIcon />} 
                                        onClick={handleInspectClick} 
                                        className="inspect-details-button" 
                                    > 
                                        {`${t('resultDisplay.inspectContent')} ${getTaskDisplayName(activeTaskKey, t)}`} 
                                    </Button>
                                ) : (
                                    <span className="no-details-message"> 
                                        {t('resultDisplay.noRegionsFlagged', { taskName: getTaskDisplayName(activeTaskKey, t) })} 
                                    </span>
                                )} 
                            </div> 
                        </div>
                    )}

                </div>
            </div>

            <VerificationModal
                isOpen={isVerificationModalOpen}
                onClose={() => setIsVerificationModalOpen(false)}
                verificationUrl={verificationUrl}
                qrCodeImageUrl={qrCodeImageUrl} // Pass updated image URL
                isGuest={isGuest}
                exactHash={activeResult.exact_hash}
                contentType={contentType} 
                contextHash={activeResult?.context_hash}
            />
        </>
    );
};

export default ResultDisplay;